<template>
    <div>
        <v-card>
            <v-progress-linear indeterminate v-if="loadInProgress||saving"></v-progress-linear>
            <v-toolbar dark color="primary">
                <v-spacer></v-spacer>
                <v-toolbar-title>Medical Record ID: {{medicalRecordID}}</v-toolbar-title>
                <v-spacer></v-spacer>
            </v-toolbar>
            <v-form @submit.prevent ref="editForm" v-model="addMedicalRecordValid">
                <v-row class="dialog-content">
                    <v-col cols="12" md="6" offset-md="3">
                        <v-row >
                            <v-col cols="12">
                                <v-menu
                                    ref="dateMenu"
                                    v-model="dateMenu"
                                    :close-on-content-click="false"
                                    :nudge-right="40"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="290px">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            v-model="displayDate"
                                            label="Date of Occurrence"
                                            prepend-icon="event"
                                            readonly
                                            v-bind="attrs"
                                            v-on="on"
                                            :disabled="medicalRecord.Closed||!canAddEditMedicalRecords"
                                        ></v-text-field>
                                    </template>
                                    <v-date-picker 
                                        v-model="medicalRecord.Date"
                                        @input="dateMenu = false"
                                        :max="maxDate"
                                        :disabled="medicalRecord.Closed||!canAddEditMedicalRecords"
                                        :title-date-format="datePickerFormat"
                                    >
                                    </v-date-picker>
                                </v-menu>
                            </v-col>
                            <v-col cols="12" v-if="medicalRecord.FollowupListID">
                                <v-menu
                                    ref="followupDateMenu"
                                    v-model="followupDateMenu"
                                    :close-on-content-click="false"
                                    :nudge-right="40"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="290px">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            v-model="displayFollowupDate"
                                            label="Followup Date"
                                            readonly
                                            prepend-icon="event"
                                            v-bind="attrs"
                                            v-on="on"
                                            :disabled="medicalRecord.Closed||!canAddEditMedicalRecords"
                                        ></v-text-field>
                                    </template>
                                    <v-date-picker 
                                        v-model="medicalRecord.FollowupDate"
                                        @input="dateMenu = false"
                                        :title-date-format="datePickerFormat"
                                    >
                                    </v-date-picker>
                                </v-menu>
                            </v-col>
                            <v-col cols="12">
                                <v-autocomplete
                                    :items="observationTypes"
                                    item-text="MedicalObservationTypeName"
                                    item-value="MedicalObservationTypeID"
                                    v-model="medicalRecord.MedicalObservationTypeID"
                                    :rules="[rules.selectIDRequired]"
                                    label="Medical Observation Type"
                                    :disabled="medicalRecord.Closed||!canAddEditMedicalRecords"
                                ></v-autocomplete>
                            </v-col>
                            <v-col cols="12">
                                <v-text-field
                                    v-if="medicalRecord.MedicalObservationTypeID===26"
                                    clearable
                                    v-model="medicalRecord.OtherTypeDescription"
                                    :rules="[rules.required]"
                                    label="Description"
                                    :disabled="medicalRecord.Closed||!canAddEditMedicalRecords">
                                </v-text-field>
                            </v-col>
                            <v-col cols="12">
                                <v-switch
                                    v-model="medicalRecord.RestFromExtraction"
                                    label="Rest From Extraction"
                                    color="red"
                                    :disabled="medicalRecord.Closed||!canAddEditMedicalRecords"
                                >
                                </v-switch>
                                <v-checkbox 
                                    label="Deceased" 
                                    v-model="medicalRecord.Deceased"
                                    @change="handleDeceasedChange"
                                    :disabled="medicalRecord.Closed||deceasedOrEuthanised||!canAddEditMedicalRecords"
                                >
                                </v-checkbox>
                                <v-checkbox 
                                    label="Euthanised" 
                                    v-model="medicalRecord.Euthanised"
                                    @change="handleEuthanisedChange"
                                    :disabled="medicalRecord.Closed||deceasedOrEuthanised||!canAddEditMedicalRecords"
                                >
                                </v-checkbox>
                            </v-col>
                            <v-col cols="12">
                                <v-textarea
                                    v-model="medicalRecord.AdditionalDetails"
                                    label="Additional Details"
                                    :disabled="medicalRecord.Closed||!canAddEditMedicalRecords"
                                >
                                </v-textarea>
                            </v-col>
                            <v-col cols="12">
                                <label class="v-label theme--light">Images</label>
                                <MedicalRecordExistingImageList
                                    @addFile="saveImage"
                                    @removeFile="deleteImage"
                                    :closedMedicalRecord="medicalRecord.Closed"
                                    :medicalRecordID="medicalRecordID"
                                    :canAddFiles="canAddEditMedicalRecords"
                                >
                                </MedicalRecordExistingImageList>
                                <label class="v-label theme--light">Reports</label>
                                <MedicalRecordExistingReportList
                                    @addFile="saveFile"
                                    @removeFile="deleteFile"
                                    :closedMedicalRecord="medicalRecord.Closed"
                                    :medicalRecordID="medicalRecordID"
                                    :canAddFiles="canAddEditMedicalRecords"
                                >
                                </MedicalRecordExistingReportList>
                            </v-col>
                            <v-col cols="12">
                                <v-autocomplete
                                    :items="sortedKeywords"
                                    item-text="Description"
                                    item-value="keywordID"
                                    v-model="medicalRecord.Keywords"
                                    label="Keywords"
                                    :rules="[rules.selectIDRequiredMultiple]"
                                    multiple
                                    :disabled="medicalRecord.Closed || !canAddEditMedicalRecords"
                                ></v-autocomplete>
                            </v-col>
                        </v-row>
                        <v-col cols="12">
                            <v-card class="pa-0">
                                <v-expansion-panels>
                                    <v-expansion-panel>
                                        <v-expansion-panel-header>
                                            <div>Change History</div>
                                        </v-expansion-panel-header>
                                        <v-expansion-panel-content>
                                            <ChangeHistory
                                                :medicalRecordID="medicalRecordID"
                                            >
                                            </ChangeHistory>
                                        </v-expansion-panel-content>
                                    </v-expansion-panel>
                                </v-expansion-panels>
                            </v-card>
                        </v-col>
                        <v-row class="dialog-action-buttons">
                            <v-col cols="12" sm="2">
                                <v-btn block color="error" :disabled="saving" @click="handleConfirmDialog">{{cancelButtonText}}</v-btn>
                            </v-col>
                            <v-col  v-if="!medicalRecord.Closed&&auth.hasPermissions([permissions.ACCESS_LEVELS.VET_MED_TEAM], true)" cols="12" sm="3">
                                <v-btn block color="success"  :disabled="!addMedicalRecordValid" :loading="saving"  @click="showFollowupComment">Save</v-btn>
                            </v-col>
                            <v-col cols="12" sm="3" v-if="!medicalRecord.FollowupListID&&!medicalRecord.Closed&&auth.hasPermissions([permissions.ACCESS_LEVELS.VET_MED_TEAM], true)" >
                                <v-btn block color="success" :disabled="!addMedicalRecordValid" :loading="saving" @click="showFollowupDate">Save & Followup</v-btn>
                            </v-col>
                            <v-col cols="12" sm="3" v-if="!medicalRecord.Closed&&auth.hasPermissions([permissions.ACCESS_LEVELS.VET_MED_TEAM], true)">
                                <v-btn block color="success" :loading="saving" :disabled="!addMedicalRecordValid" @click="showCloseDialog">Save & Close</v-btn>
                            </v-col>
                            <v-col cols="12" sm="3" v-if="!medicalRecord.FollowupComplete&&medicalRecord.FollowupListID&&!medicalRecord.Closed&&auth.hasPermissions([permissions.ACCESS_LEVELS.VET_MED_TEAM], true)">
                                <v-btn block color="error" :disabled="!addMedicalRecordValid" :loading="saving" @click="CloseFollowup">Complete Followup</v-btn>
                            </v-col>
                            <v-col cols="12" v-if="saveError">
                                <v-alert text type="error">{{ saveError }}</v-alert>
                            </v-col>
                            <v-col cols="12" class="text-center" v-if="fileError">
                                <v-alert text type="error">{{fileError}}</v-alert>
                            </v-col>
                        </v-row>                            
                    </v-col>
                </v-row>
            </v-form>
        </v-card>
        <FollowupDateDialog :followupDateDialog="followupDateDialog" @save="saveMedicalRecord" @cancel="hideDateDialog">
        </FollowupDateDialog>
        <FollowupComment :dialog="commentDialog" :editMode="editMode" @save="saveMedicalRecord" @cancel="hideFollowupComment">
        </FollowupComment>
        <MedicalRecordCloseDialog
            :dialog="closeDialog"
            @save="saveMedicalRecord"
            @cancel="hideCloseDialog"
        >
        </MedicalRecordCloseDialog>
        <ConfirmDialog
            :dialog="confirmDialog"
            @confirm="returnToMedicalPage"
            @cancel="confirmDialog = false"
        >
        </ConfirmDialog>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import { mapGetters } from 'vuex';
import moment from 'moment';
import * as auth from '../../services/auth';
import util from '@/helpers/util';
import validationRules from "../../helpers/validationrules";
import permissions from 'comunet-btg-utah-repository-shared/permissions';
import constants from 'comunet-btg-utah-repository-shared/constants';
import medicalAPI from '../../services/medicalRecordAPI';
import fileService from '../../services/fileService';
import FollowupComment from "./FollowupComment.vue";
import MedicalRecordExistingImageList from './MedicalRecordExistingImageList.vue'
import MedicalRecordExistingReportList from "./MedicalRecordExistingReportList.vue";
import ChangeHistory from "./ChangeHistory.vue";
import MedicalRecordCloseDialog from "./CloseDialog.vue";
import ConfirmDialog from "./ConfirmReturnDialog.vue";
import FollowupDateDialog from "./FollowupDateDialog.vue";
export default {
    components: {
        FollowupComment,
        MedicalRecordExistingReportList,
        ChangeHistory,
        MedicalRecordCloseDialog,
        ConfirmDialog,
        FollowupDateDialog,
        MedicalRecordExistingImageList
    },
    props: {
        specimenID: {
            type: String,
            default: ""
        },
        medicalRecordID: {
            type: String,
            default: ""
        }    
    } ,
    data() {
        return {
            medicalRecord:{
            },
            auth:auth,
            rules:validationRules.rules,
            permissions:permissions,
            photos:[],
            dateMenu: false,
            followupDateMenu: false,
            allowedFileExtensions: '.pdf',
            allowedImageExtensions: '.JPEG, .PNG, .JPG',
            fileError: "",
            pendingFile: null,
            pendingImage: null,
            saving: false,
            images: [],
            files: [],
            followups: [],
            commentDialog: false,
            closeMedicalRecord: false,
            followupDateDialog: false,
            closeDialog: false,
            confirmDialog: false,
            deceasedOrEuthanised:false,
            addMedicalRecordValid: false,
            editMode: true,
            keywords: [
               {
                    KeywordID: 0,
                    Description: "Necropsy"
               },
               {
                    KeywordID: 1,
                    Description: "Spinal Lesion"
               },
               {
                    KeywordID: 2,
                    Description: "Fracture"
               },
               {
                    KeywordID: 3,
                    Description: "Retained Ova"
               },
               {
                    KeywordID: 4,
                    Description: "Mycobacteria"
               },
               {
                    KeywordID: 5,
                    Description: "Surgery"
               },
               {
                    KeywordID: 6,
                    Description: "Spay/Neuter"
               },
               {
                    KeywordID: 7,
                    Description: "2 x Refusal"
               },
               {
                    KeywordID: 8,
                    Description: "5 x Refusal"
               },
               {
                    KeywordID: 9,
                    Description: "TF"
               }
            ]
        };
    },
    computed: {
         ...mapGetters({
            medicalObservationTypes: "dailyHusbandryStore/getMedicalObservationTypes",
            loadInProgress: "medicalRecordStore/loadInProgress"
        }),

        displayDate: {
            get: function() {
                if (this.medicalRecord && this.medicalRecord.Date) {
                    return moment.utc(this.medicalRecord.Date).format(constants.DATE_FORMAT_NO_TIME);
                }
                
                return "";
            },
            set: function(newValue) {
                this.medicalRecord.Date = newValue;
            }
        },
        displayFollowupDate: {
            get: function() {
                if (this.medicalRecord && this.medicalRecord.FollowupDate) {
                    return moment.utc(this.medicalRecord.FollowupDate).format(constants.DATE_FORMAT_NO_TIME);
                }
                
                return "";
            },
            set: function(newValue) {
                this.medicalRecord.FollowupDate = newValue;
            }
        },
        maxDate() {
            return moment().toISOString();
        },
        observationTypes() {
            return [...this.$store.state.dailyHusbandryStore.medicalObservationTypes].sort(util.sortByField("MedicalObservationTypeName",["Other"]));
        },
        sortedKeywords() {
            return [...this.keywords].sort(util.sortByField("Description"));
        },
        canAddEditMedicalRecords() {
            return auth.hasPermissions([permissions.ACCESS_LEVELS.VET_MED_TEAM], true);
        },
        cancelButtonText() {
            return this.medicalRecord.Closed || !this.canAddEditMedicalRecords ? "RETURN" : "CANCEL";
        }
    },
    methods: {
        datePickerFormat(date) {
            return moment.utc(date).format(constants.DATE_FORMAT_NO_TIME);
        },
        showFollowupComment() {
            this.commentDialog = true;
        },
        hideFollowupComment() {
            this.closeMedicalRecord = false;
            this.medicalRecord.Closed = false;
            this.medicalRecord.FollowupDate = null;
            this.commentDialog = false;
        },
        showCloseDialog() {
            this.closeMedicalRecord = true;
            this.closeDialog = true;
        },
        hideCloseDialog() {
            this.closeMedicalRecord = false;
            this.medicalRecord.Closed = false;
            this.closeDialog = false;
        },
        handleDeceasedChange() {
            if(this.medicalRecord.Euthanised){
                this.medicalRecord.Euthanised = 0;
            }
        },
        showFollowupDate() {
            this.followupDateDialog = true;
        },
        hideDateDialog() {
            this.followupDateDialog = false;
        },
        handleEuthanisedChange() {
            if(this.medicalRecord.Deceased){
                this.medicalRecord.Deceased = 0;
            }
        },
        returnToMedicalPage() {
            this.confirmDialog = false;
            this.redirectToMedicalRecords();
        },
        cancel() {
            if(this.medicalRecord.Closed) {
                this.returnToMedicalPage();
            } else {
                this.confirmDialog = true;
            }
        },
        redirectToMedicalRecords() {
            this.$router.push({ name: 'medicalRecords'});
        },
        saveImage(image) {
            this.images.push(image);
        },
        deleteImage(image) {
            const removingIndex = this.images.indexOf(image)
            this.images.splice(removingIndex, 1);
        },
        saveFile(file) {
            this.files.push(file);
        },
        deleteFile(file) {
            const removingIndex = this.files.indexOf(file);
            this.files.splice(removingIndex, 1);
        },
        handleConfirmDialog() {
            if(this.medicalRecord.Closed || !this.canAddEditMedicalRecords) {
                this.returnToMedicalPage();
            } else {
                this.confirmDialog = true;
            }
        },
        async saveMedicalRecord(newFollowup, followupItem, followupAssignedVet) {
            this.saveError = "";
            if(followupItem !== 'save') {
                if(newFollowup) {
                    this.medicalRecord.FollowupDate = followupItem;
                    this.medicalRecord.AssignedVetUser = followupAssignedVet;
                    this.followupDateDialog = false;
                    this.showFollowupComment();
                    return;
                } else {
                    this.medicalRecord.followupComment = followupItem;
                }
            }

            if(this.closeMedicalRecord) {
                this.medicalRecord.Closed = true;
                this.closeDialog = false;
                this.closeMedicalRecord = false;
                this.showFollowupComment();
                return;
            }
            this.saving = true;
            try {

                if(this.deceasedOrEuthanised) {
                    delete this.medicalRecord.Deceased;
                    delete this.medicalRecord.Euthanised;
                }
                
                // Add the data to the database
                await medicalAPI.saveMedicalRecord(this.medicalRecord);
                // Add the files
               if (this.files.length > 0) {
                    await Promise.all(this.files.map(async (file) => {
                        let document = {
                            MedicalRecordID: this.medicalRecord.MedicalRecordID,
                            FileName: file.FileName
                        };

                        await fileService.uploadFileAndSaveDocument(document, file.FileName, "medicalRecords", file.ContentType, file.FileData);
                    }))
                }
                 //Add the images
                if(this.images.length > 0) {
                    await Promise.all(this.images.map(async (image) => {
                        let uploadImage = {
                            MedicalRecordID: this.medicalRecord.MedicalRecordID,
                            ImageName: image.FileName
                        };
                        await fileService.uploadMedicalRecordImage(uploadImage, image.FileName, "medicalRecords", image.ContentType, image.FileData);
                    }))
                }

                this.redirectToMedicalRecords();
                await this.$store.dispatch("medicalRecordStore/loadFollowupsOfCurrentUser",{allFollowupsOfCurrentUser: true, allVetFollowups: false});
            } catch (e) {
                this.saveError = e.message;
            } 
                this.saving = false;
                this.hideCloseDialog();
                this.commentDialog = false;
        },
        async loadMedicalRecord() {
            this.saving = true;
            this.saveError = "";

            try {
                // get a medical record
                const result = await medicalAPI.getMedicalRecords({medicalRecordID: this.medicalRecordID});
                const MedicalRecord = [...result.data.MedicalRecords];
                if(MedicalRecord[0].Keywords) {
                    MedicalRecord[0].Keywords = MedicalRecord[0].Keywords.split(",");
                }
                this.medicalRecord = MedicalRecord[0];
            } catch (e) {
                this.saveError = e.message;
            }
            
            this.saving = false;
        },
        async CloseFollowup() {
            this.saving = true;
            this.saveError = "";

            try {
                // close a followup
                await medicalAPI.closeFollowup(this.medicalRecord.FollowupListID);
                await this.loadMedicalRecord();
                await this.$store.dispatch("medicalRecordStore/loadFollowupsOfCurrentUser",{allFollowupsOfCurrentUser: true, allVetFollowups: false});
            } catch (e) {
                this.saveError = e.message;
            }
            
            this.saving = false;
        }
    },
    async created() {
          this.$store.dispatch("dailyHusbandryStore/loadMedicalObservationTypes");
          if(this.medicalRecordID) {
              await this.loadMedicalRecord();
          }
          if(this.medicalRecord.Deceased || this.medicalRecord.Euthanised) {
              this.deceasedOrEuthanised = true;
          }
          this.$nextTick(() => this.$refs.editForm.validate());
    },
}
</script>
